/**
 * Investor-Portal — Segment MATERIAL / VERPACKUNG (#1178 v2.564.0).
 *
 * Handelsmarge — KEIN Personal, KEIN Finecom-RevShare.
 * Marge = Verkauf (invoice) − Verbrauch (vendor_bill + other) auf KSt [5].
 *
 * Manuel-Decision: simpler Saldo, KEINE 3-Zustands-Logik, KEINE Fehler-Badges.
 *
 * Befund 2026 YTD: +99k inv, −87k vb, +8,6k other (March Bestandsveränderung)
 *                  → Marge ~ 20.394 € (20,6 %).
 *
 * Layout:
 *   - PeriodToggle
 *   - 4 KPI-Tiles (Verkauf / Verbrauch / Marge / Marge %)
 *   - AT/DE/Σ-Verkaufs-Tabelle
 *   - Stück-KPIs: Umsatz+Marge pro Karton + pro Meter Füllmaterial
 *   - Top-Lieferanten
 *   - Monats-Trend
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { TrendingUp, Wallet, BarChart3, Percent } from 'lucide-react';
import PeriodToggle, { buildPeriodQuery } from './components/PeriodToggle';
import DataMaturityBanner from './components/DataMaturityBanner';
import { MaturityOverlay, reifeFooter } from './components/MaturityChartOverlay';
import { defaultMatureMonthState, applyYtdCutoff } from '../../utils/data-maturity';

const API = import.meta.env.VITE_API_URL || '';
const MONTH_LABELS = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];

function fmtEur(v) { if (v==null||isNaN(v)) return '–'; return new Intl.NumberFormat('de-AT',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(v); }
function fmtEurDetail(v) { if (v==null||isNaN(v)) return '–'; return new Intl.NumberFormat('de-AT',{style:'currency',currency:'EUR',maximumFractionDigits:2}).format(v); }
function fmtEurCent(v) { if (v==null||isNaN(v)) return '—'; return new Intl.NumberFormat('de-AT',{style:'currency',currency:'EUR',minimumFractionDigits:2,maximumFractionDigits:2}).format(v); }
function fmtPct(v) { if (v==null||isNaN(v)) return '–'; return `${v.toFixed(1)} %`; }
function fmtInt(v) { if (v==null||isNaN(v)) return '—'; return new Intl.NumberFormat('de-AT',{maximumFractionDigits:0}).format(v); }

export default function InvestorSegmentMaterial() {
  const [searchParams] = useSearchParams();
  const currentYear = new Date().getFullYear();

  const [periodState, setPeriodState] = useState(() => {
    // #1184 v2.569.0 — Default = letzter belastbarer Monat
    const mature = defaultMatureMonthState();
    return {
      period: searchParams.get('period') || 'month',
      year: parseInt(searchParams.get('year') || mature.year, 10),
      month: parseInt(searchParams.get('month') || mature.month, 10),
      quarter: parseInt(searchParams.get('quarter') || Math.ceil((new Date().getMonth() + 1) / 3), 10),
      fromDate: searchParams.get('from_date') || null,
      toDate: searchParams.get('to_date') || null,
    };
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [yearTrend, setYearTrend] = useState(null);
  const [yearTrendLoading, setYearTrendLoading] = useState(true);
  const [yearTrendError, setYearTrendError] = useState(null);

  // #1184 v2.569.0 — YTD frontend-seitig auf belastbaren Cutoff verkürzen
  const effectivePeriodState = useMemo(() => applyYtdCutoff(periodState), [periodState]);
  const periodQuery = useMemo(() => buildPeriodQuery(effectivePeriodState), [effectivePeriodState]);

  useEffect(() => {
    let abort = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const q = new URLSearchParams(periodQuery);
        const res = await fetch(`${API}/api/investor/segmente/material?${q.toString()}`, { credentials: 'include' });
        const json = await res.json();
        if (abort) return;
        if (!res.ok) { setError(json.error || `HTTP ${res.status}`); setData(null); } else setData(json);
      } catch (e) { if (!abort) setError(e.message); }
      finally { if (!abort) setLoading(false); }
    }
    load(); return () => { abort = true; };
  }, [periodQuery]);

  useEffect(() => {
    let abort = false;
    async function load() {
      setYearTrendLoading(true); setYearTrendError(null);
      try {
        const res = await fetch(`${API}/api/investor/segmente/material/jahrestrend?year=${periodState.year}`, { credentials: 'include' });
        const json = await res.json();
        if (abort) return;
        if (!res.ok) { setYearTrendError(json.error || `HTTP ${res.status}`); setYearTrend(null); } else setYearTrend(json);
      } catch (e) { if (!abort) setYearTrendError(e.message); }
      finally { if (!abort) setYearTrendLoading(false); }
    }
    load(); return () => { abort = true; };
  }, [periodState.year]);

  const chartData = useMemo(() => {
    if (!yearTrend?.monats_trend) return [];
    return yearTrend.monats_trend.map(m => ({
      monat: MONTH_LABELS[m.monat - 1],
      Verkauf: Math.round(m.umsatz || 0),
      Verbrauch: Math.round(m.aufwand || 0),
      Marge: Math.round(m.db || 0),
    }));
  }, [yearTrend]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1A2E]">Segment Material / Verpackung</h1>
          <div className="text-sm text-[#6B7280] mt-1">
            Handelsmarge auf Verpackungs- &amp; Füllmaterial — Verkauf an Kunden minus Lieferanten-Einkauf
            inklusive Bestandsveränderungs-Saldo.
          </div>
        </div>
        <PeriodToggle
          period={periodState.period} year={periodState.year} month={periodState.month}
          quarter={periodState.quarter} fromDate={periodState.fromDate} toDate={periodState.toDate}
          onChange={setPeriodState}
        />
      </div>

      {/* #1184 v2.569.0 — Datenreife-Banner */}
      <DataMaturityBanner periodState={periodState} />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">Fehler: {error}</div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile label="Verkauf" value={fmtEur(data?.marge?.verkauf)} sub="Kundenrechnungen Material" loading={loading}
          Icon={TrendingUp} iconColor="#338edc" iconTint="#EBF5FD" />
        <KpiTile label="Verbrauch" value={fmtEur(data?.marge?.verbrauch)} sub="Einkauf + Bestandsveränderung" loading={loading}
          Icon={Wallet} iconColor="#EF4444" iconTint="#FEE2E2" />
        <KpiTile label="Marge" value={fmtEur(data?.marge?.db)} sub="Verkauf − Verbrauch" loading={loading}
          Icon={BarChart3} iconColor="#10B981" iconTint="#D1FAE5" />
        <KpiTile label="Marge %" value={fmtPct(data?.marge?.marge_pct)} sub="Marge ÷ Verkauf" loading={loading}
          Icon={Percent} iconColor="#6366F1" iconTint="#E0E7FF" />
      </div>

      {/* Verbrauchs-Aufschlüsselung */}
      {data?.verbrauch && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
          <div className="text-sm font-semibold text-[#1A1A2E] mb-2">Verbrauchs-Komponenten</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="border border-[#E5E7EB] rounded-lg p-3 bg-[#FAFBFC]">
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">Lieferanten-Einkauf</div>
              <div className="text-lg font-semibold text-[#1A1A2E] tabular-nums">{fmtEurDetail(data.verbrauch.vendor_bill)}</div>
              <div className="text-[11px] text-[#9CA3AF] mt-1">vendor_bill</div>
            </div>
            <div className="border border-[#E5E7EB] rounded-lg p-3 bg-[#FAFBFC]">
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">Bestandsveränderung</div>
              <div className="text-lg font-semibold text-[#1A1A2E] tabular-nums">{fmtEurDetail(data.verbrauch.other)}</div>
              <div className="text-[11px] text-[#9CA3AF] mt-1">other (Saldo Kl1 ↔ Kl5)</div>
            </div>
            <div className="border border-[#E5E7EB] rounded-lg p-3 bg-[#FAFBFC]">
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">Gesamt</div>
              <div className="text-lg font-semibold text-[#1A1A2E] tabular-nums">{fmtEurDetail(data.verbrauch.gesamt)}</div>
              <div className="text-[11px] text-[#9CA3AF] mt-1">Σ</div>
            </div>
          </div>
        </div>
      )}

      {/* Verkaufs-Standort-Tabelle */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[#1A1A2E]">Verkauf-Split (Kundenseite)</h2>
          {data && (
            <span className="text-[11px] text-[#9CA3AF]">
              {data.lines_processed} Buchungen · {data.period?.dateFrom} – {data.period?.dateTo}
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-24">Standort</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Verkauf</th>
                <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Anteil</th>
              </tr>
            </thead>
            <tbody>
              {loading && !data && (
                <tr><td colSpan={3} className="px-3 py-6 text-center text-[#9CA3AF]">Lade Odoo-Daten…</td></tr>
              )}
              {!loading && data?.standorte?.map(s => (
                <tr key={s.standort}
                  className={`border-t border-[#F3F4F6] ${s.standort === 'GESAMT' ? 'bg-[#F8F9FC] font-semibold' : 'hover:bg-[#FAFBFC]'}`}>
                  <td className="px-3 py-2 text-[#1A1A2E]">{labelFor(s.standort)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(s.umsatz)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtPct(s.umsatz_anteil_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stück-KPIs */}
      {data?.stueck_kpis && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl">
          <div className="px-4 py-3 border-b border-[#E5E7EB]">
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Stück-Kennzahlen</h2>
            <div className="text-[11px] text-[#9CA3AF] mt-0.5">
              Marge proportional zum Umsatz-Anteil auf Karton vs. Füllmaterial verteilt
              (Verbrauch ist auf KS-Ebene gebucht, nicht produktscharf).
            </div>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border border-[#E5E7EB] rounded-lg p-3 bg-[#FAFBFC]">
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-2">Karton (alles außer Füllmaterial)</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] uppercase text-[#338edc] font-semibold tracking-wider">Umsatz/Stück</div>
                  <div className="text-lg font-semibold text-[#1A1A2E] tabular-nums">{fmtEurCent(data.stueck_kpis.umsatz_per_karton)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[#10B981] font-semibold tracking-wider">Marge/Stück</div>
                  <div className="text-lg font-semibold text-[#1A1A2E] tabular-nums">{fmtEurCent(data.stueck_kpis.marge_per_karton)}</div>
                </div>
              </div>
              <div className="text-[11px] text-[#9CA3AF] mt-2">{fmtInt(data.stueck_kpis.karton_menge)} Stück · {fmtEur(data.stueck_kpis.karton_umsatz)}</div>
            </div>
            <div className="border border-[#E5E7EB] rounded-lg p-3 bg-[#FAFBFC]">
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-2">Füllmaterial (Meter)</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] uppercase text-[#338edc] font-semibold tracking-wider">Umsatz/Meter</div>
                  <div className="text-lg font-semibold text-[#1A1A2E] tabular-nums">{fmtEurCent(data.stueck_kpis.umsatz_per_meter)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-[#10B981] font-semibold tracking-wider">Marge/Meter</div>
                  <div className="text-lg font-semibold text-[#1A1A2E] tabular-nums">{fmtEurCent(data.stueck_kpis.marge_per_meter)}</div>
                </div>
              </div>
              <div className="text-[11px] text-[#9CA3AF] mt-2">{fmtInt(data.stueck_kpis.fuell_meter)} Meter · {fmtEur(data.stueck_kpis.fuell_umsatz)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Top-Lieferanten */}
      {data?.top_lieferanten && !data.top_lieferanten.error && data.top_lieferanten.top5?.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Top-Lieferanten (Einkauf)</h2>
            <span className="text-[11px] text-[#9CA3AF]">
              {data.top_lieferanten.lieferanten_count} Lieferanten · Σ Einkauf {fmtEurDetail(data.top_lieferanten.total_einkauf)}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-8">#</th>
                  <th className="text-left px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Lieferant</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Einkauf</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-24">Anteil</th>
                </tr>
              </thead>
              <tbody>
                {data.top_lieferanten.top5.map((r, i) => (
                  <tr key={r.partner_id} className="border-t border-[#F3F4F6] hover:bg-[#FAFBFC]">
                    <td className="px-3 py-2 text-[#9CA3AF]">{i + 1}</td>
                    <td className="px-3 py-2 text-[#1A1A2E]">{r.name}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(r.einkauf)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">{fmtPct(r.anteil_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Monats-Trend */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl">
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Monats-Trend {periodState.year}</h2>
            <div className="text-[11px] text-[#9CA3AF] mt-0.5">
              Verkauf · Verbrauch · Marge — nur Monate mit Material-Buchungen.
            </div>
          </div>
          {yearTrendError && <span className="text-[11px] text-red-600">Fehler: {yearTrendError}</span>}
        </div>
        <div className="p-4">
          {yearTrendLoading && !yearTrend && (
            <div className="h-[320px] flex items-center justify-center text-[#9CA3AF] text-sm">Lade Jahres-Trend…</div>
          )}
          {!yearTrendLoading && chartData.length === 0 && (
            <div className="h-[320px] flex items-center justify-center text-[#9CA3AF] text-sm">Keine Daten für {periodState.year}.</div>
          )}
          {chartData.length > 0 && reifeFooter(periodState.year) && (
            <div className="text-[11px] text-[#9CA3AF] mb-2">
              {reifeFooter(periodState.year)}
            </div>
          )}
          {chartData.length > 0 && (
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                  <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="monat" tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={false}
                    tickFormatter={v => new Intl.NumberFormat('de-AT', { notation: 'compact', maximumFractionDigits: 1 }).format(v) + ' €'} />
                  <Tooltip formatter={v => fmtEurDetail(v)}
                    labelStyle={{ color: '#1A1A2E', fontWeight: 600 }}
                    contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
                  {/* #1184 v2.569.0 — Vorläufige Monate markieren */}
                  {MaturityOverlay({ year: periodState.year, monthLabels: MONTH_LABELS })}
                  <Line type="monotone" dataKey="Verkauf" stroke="#338edc" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="Verbrauch" stroke="#EF4444" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="Marge" stroke="#10B981" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="text-xs text-[#9CA3AF]">
        Quelle: Odoo <code>account.analytic.line</code> · Kostenstelle Verpackungs- &amp; Füllmaterial (5) ·
        Verbrauch = Σ <code>vendor_bill</code> + Σ <code>other</code> (Bestandsveränderung-Saldo).
        Füllmaterial-Mengen aus <code>unit_amount</code> auf Produkt #235.
      </div>
    </div>
  );
}

function labelFor(s) {
  if (s === 'AT') return 'Österreich';
  if (s === 'DE') return 'Deutschland';
  if (s === 'GESAMT') return 'Gesamt';
  return s;
}

function KpiTile({ label, value, sub, loading, Icon, iconColor, iconTint }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">{label}</div>
        {Icon && (
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
            style={{ backgroundColor: iconTint || '#F3F4F6' }} aria-hidden="true">
            <Icon width={16} height={16} stroke={iconColor || '#6B7280'} strokeWidth={2} fill="none" />
          </span>
        )}
      </div>
      <div className="text-2xl font-semibold text-[#1A1A2E] tabular-nums">
        {loading ? <span className="inline-block w-24 h-7 rounded bg-[#F3F4F6] animate-pulse" /> : value}
      </div>
      {sub && <div className="text-[11px] text-[#9CA3AF] mt-1">{sub}</div>}
    </div>
  );
}
